import type { QueuedRealtimeInternalMessage } from './realtime-voice-agent';

/** Admit one narration at a time, rechecking questions after any buffered delay. */
export function createRealtimeVoiceInternalMessageQueue(dependencies: {
  canDeliver: () => boolean;
  appendFacts: (item: QueuedRealtimeInternalMessage) => boolean;
  requestSpeech: (item: QueuedRealtimeInternalMessage) => boolean;
  maxPending?: number;
}) {
  type Entry = {
    item: QueuedRealtimeInternalMessage;
    factsAccepted: boolean;
    validating: boolean;
    attempts: number;
  };
  const pending = new Map<string, Entry>();
  const delivered = new Set<string>();
  const deliver = (key: string, entry: Entry) => {
    if (pending.get(key) !== entry || !dependencies.canDeliver()) return;
    if (!entry.factsAccepted) {
      if (!dependencies.appendFacts(entry.item)) return;
      entry.factsAccepted = true;
    }
    if (!dependencies.requestSpeech(entry.item)) return;
    pending.delete(key);
    delivered.add(key);
    if (delivered.size > 512) {
      const oldest = delivered.values().next().value;
      if (oldest !== undefined) delivered.delete(oldest);
    }
    entry.item.onDelivered?.();
  };
  const flush = (attempted = new Set<Entry>()) => {
    if (!dependencies.canDeliver()) return;
    const first = [...pending].find(([, entry]) => !attempted.has(entry));
    if (!first) return;
    const [key, entry] = first;
    if (entry.validating) return;
    if (!entry.item.validate) return deliver(key, entry);
    attempted.add(entry);
    const discard = () => {
      if (pending.get(key) !== entry) return;
      pending.delete(key);
      entry.item.onDiscarded?.();
      flush(attempted);
    };
    entry.validating = true;
    entry.attempts += 1;
    void entry.item
      .validate()
      .then((current) => {
        if (pending.get(key) !== entry) return;
        entry.validating = false;
        if (current) deliver(key, entry);
        else discard();
      })
      .catch(() => {
        if (pending.get(key) !== entry) return;
        entry.validating = false;
        pending.delete(key);
        if (
          entry.attempts >= 3 &&
          !entry.factsAccepted &&
          entry.item.onDeferred
        ) {
          entry.item.onDeferred();
        } else {
          // Rotate transient failures so other narration can proceed. Callers
          // without persisted retry admission retain their pending prompt.
          pending.set(key, entry);
        }
        flush(attempted);
      });
  };
  return {
    enqueue(item: QueuedRealtimeInternalMessage) {
      if (delivered.has(item.key)) return true;
      if (!pending.has(item.key)) {
        if (pending.size >= (dependencies.maxPending ?? 64)) return false;
        pending.set(item.key, {
          item,
          factsAccepted: false,
          validating: false,
          attempts: 0,
        });
      }
      flush();
      return true;
    },
    flush,
    reset() {
      pending.clear();
      delivered.clear();
    },
  };
}
